import {Component, ElementRef, Input, OnInit, ViewChild} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';

import {of} from 'rxjs';
import {catchError, filter, finalize, map, switchMap, takeUntil, tap} from 'rxjs/operators';

import {Destroyable} from '../../../../destroyable.component';
import {CreateUserData} from '../../../../models/models.interfaces';
import {Contract, Employee} from '../../../employees/employees.model';
import {EmployeesService} from '../../../employees/employees.service';
import {Validators as GpValidators} from '../../../forms';
import {ModalService} from '../../../modal';
import {UserService} from '../../index';

@Component({
  selector: 'gp-create-user-modal',
  templateUrl: './create-user-modal.component.html'
})
export class CreateUserModalComponent extends Destroyable implements OnInit {

  @Input()
  public employee: Employee;
  @Input()
  public contract: Contract;

  public userCreateForm: UntypedFormGroup;
  public emailValidation: boolean = false;
  public isEmployee = false;

  public loading: boolean = false;

  public defaultProfile: string;

  @ViewChild('emailInput', {static: true})
  public emailInput: ElementRef;

  public mvelAliases = ['recipientEmail'];

  constructor(private userService: UserService,
              private formBuilder: UntypedFormBuilder,
              private employeesService: EmployeesService,
              private modalService: ModalService) {
    super();
  }

  ngOnInit() {
    this.emailInput.nativeElement.focus();
    this.createForm();
    this.employeesService.getOfferingDefaultProfile()
    .pipe(
      catchError(() => of(null))
    ).subscribe(profile => {
      this.defaultProfile = profile || '';
    });

    this.userCreateForm.get('email').valueChanges
      .pipe(
        filter((value) => !!value),
        switchMap((value) => this.userService.getNonEmpUserIdByEmail(value)),
        tap((userId: number) => {
          this.isEmployee = !userId;
        }),
        takeUntil(this.componentDestroyed$)
      ).subscribe();
  }

  public submit(): void {
    const form: UntypedFormGroup = this.userCreateForm;
    const email: string = form.get('email').value;
    const isActivateOnce: boolean = form.get('isActivateOnce').value;
    const userToCreate: CreateUserData = {
      cliEmpId: this.employee.id,
      login: email,
      firstName: this.employee.firstName,
      lastName: this.employee.lastName,
      email,
      active: isActivateOnce,
      profileName: this.defaultProfile
    };
    this.createUser(userToCreate);
  }

  public getEmployeeContractId() {
    if (this.contract) {
      return this.contract.id;
    }
  }

  private createUser(userToCreate: CreateUserData) {
    this.loading = true;
    this.userService.createUser(userToCreate)
      .pipe(
        switchMap(() => this.employeesService.getEmployeeById(this.employee.id)),
        finalize(() => this.loading = false))
      .subscribe((result) => {
        this.modalService.closeTopmostModal();
        this.userService.createdUser.next(result);
      });
  }

  private createForm(): void {
    this.userCreateForm = this.formBuilder.group({
      email: ['',
        [GpValidators.emailValidator, Validators.required],
        this.isEmailExistValidator.bind(this)],
      isActivateOnce: [true]
    });
  }

  private isEmailExistValidator(c: UntypedFormControl) {
    this.loading = true;
    if (!c.value) {
      return of(null);
    }

    this.emailValidation = true;
    return this.employeesService.checkEmailAvailable(c.value, this.employee.id)
      .pipe(
        finalize(() => {
          this.emailValidation = false;
          this.loading = false;
        }),
        map((status) => {
          if (!status) {
            return {emailExists: true};
          }
          return null;
        })
      );
  }
}
